export const WORKSPACE_ROLES = ['owner', 'admin', 'editor'] as const;
export const PAID_STRIPE_ROLES = ['basic', 'premium', 'business_plus'] as const;

export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number];
export type PaidStripeRole = (typeof PAID_STRIPE_ROLES)[number];

export type WorkspaceMemberStatus = 'active' | 'removed';

export interface WorkspaceMemberLike {
  role?: unknown;
  status?: unknown;
}

export interface ProjectOwnerLike {
  owner_id?: unknown;
  user_id?: unknown;
}

export function normalizeWorkspaceRole(role: unknown): WorkspaceRole | null {
  return WORKSPACE_ROLES.includes(role as WorkspaceRole) ? (role as WorkspaceRole) : null;
}

export function isPaidWorkspaceStripeRole(role: unknown): role is PaidStripeRole {
  return PAID_STRIPE_ROLES.includes(role as PaidStripeRole);
}

export function hasBasicAppAccess(
  stripeRole: unknown,
  hasBillableWorkspaceMembership: boolean,
): boolean {
  return isPaidWorkspaceStripeRole(stripeRole) || hasBillableWorkspaceMembership;
}

export function normalizeMemberStatus(status: unknown): WorkspaceMemberStatus {
  return status === 'removed' ? 'removed' : 'active';
}

export function canViewWorkspace(role: unknown): boolean {
  return normalizeWorkspaceRole(role) !== null;
}

export function canManageWorkspace(role: unknown): boolean {
  const normalized = normalizeWorkspaceRole(role);
  return normalized === 'owner' || normalized === 'admin';
}

export function canEditPromptCollectionSettings(role: unknown): boolean {
  return canManageWorkspace(role);
}

export function getActiveWorkspaceRole(member: WorkspaceMemberLike | null | undefined): WorkspaceRole | null {
  if (!member || normalizeMemberStatus(member.status) !== 'active') {
    return null;
  }
  return normalizeWorkspaceRole(member.role);
}

export function resolveLegacyDataOwnerUid(project: ProjectOwnerLike | null | undefined): string | null {
  const ownerId = typeof project?.owner_id === 'string' ? project.owner_id.trim() : '';
  if (ownerId) return ownerId;

  const userId = typeof project?.user_id === 'string' ? project.user_id.trim() : '';
  return userId || null;
}
